import {
  CreditFlagTypeOption,
  CustomerCreditReviewFlag,
  CustomerFlagPermissions,
} from '@store/customers/customerDetails';
import { DateTime } from 'luxon';

export enum CustomerFlagType {
  FlagForReview = 6,
  FlagForFinancialExposure = 7,
}

export const ASSIGNED_DEPARTMENT_OPTIONS = [
  { label: 'Collections', value: '1' },
  { label: 'Credit', value: '0' },
];

const COLLECTIONS_DEPARTMENT = 1;

export const isFinancialExposureFlag = (
  flagType?: string | number | null,
): boolean => Number(flagType) === CustomerFlagType.FlagForFinancialExposure;

export const canUseFlagType = (
  flagType: string | number | null | undefined,
  permissions: CustomerFlagPermissions,
): boolean => {
  switch (Number(flagType)) {
    case CustomerFlagType.FlagForFinancialExposure:
      return permissions.canFlagFinancialExposure;
    case CustomerFlagType.FlagForReview:
      return permissions.canFlagForReview;
    default:
      return false;
  }
};

export const buildFlagTypeOptions = (
  types: CreditFlagTypeOption[],
  permissions: CustomerFlagPermissions,
) =>
  types
    .filter((type) => canUseFlagType(type.id, permissions))
    .map((type) => ({ label: type.value, value: String(type.id) }));

export const getFlagModalTitle = (
  mode: string,
  flag: CustomerCreditReviewFlag | null,
): string => {
  if (mode !== 'edit') return 'Add Flag';
  return isFinancialExposureFlag(flag?.flagType)
    ? 'Edit Financial Exposure Flag'
    : 'Edit Credit Review Flag';
};

type FlagBannerMeta = {
  type: 'info' | 'warning';
  title: string;
  containerClass: string;
  titleClass: string;
  showReviewDate: boolean;
};

export const getFlagBannerMeta = (
  flag: CustomerCreditReviewFlag,
): FlagBannerMeta => {
  if (isFinancialExposureFlag(flag.flagType)) {
    return {
      type: 'warning',
      title: 'Customer Flagged for Financial Exposure',
      containerClass: 'cursor-pointer border-[#F59D25] bg-[#FFF4E5]',
      titleClass: 'text-[#FF8F00]!',
      showReviewDate: false,
    };
  }

  return {
    type: 'info',
    title:
      Number(flag.assignedDepartment) === COLLECTIONS_DEPARTMENT
        ? 'Customer Flagged for Collections Review'
        : 'Customer Flagged for Credit Review',
    containerClass: 'cursor-pointer border-[#468DB5] bg-[#E5F6FC]',
    titleClass: 'text-[#026EA1]!',
    showReviewDate: true,
  };
};

const CREATED_ON_FORMAT = 'MM/dd/yyyy hh:mm a';

const parseFlagDate = (value: string): DateTime | null => {
  const iso = DateTime.fromISO(value, { zone: 'utc' });
  if (iso.isValid) return iso;

  const dateOnly = DateTime.fromFormat(value, 'MM/dd/yyyy', { zone: 'utc' });
  return dateOnly.isValid ? dateOnly : null;
};

export const formatFlagCreatedOn = (value?: string | null): string => {
  if (value === undefined || value === null || value === '') return '';
  const parsed = parseFlagDate(value);
  return parsed ? parsed.toLocal().toFormat(CREATED_ON_FORMAT) : '';
};

export const buildCreatedByLabel = (flag: CustomerCreditReviewFlag): string => {
  const createdOn = formatFlagCreatedOn(flag.createDate ?? flag.flagDate);
  const createdBy = flag.flaggedBy ?? '';
  if (!createdBy && !createdOn) return '';
  return `Created By ${[createdBy, createdOn].filter(Boolean).join(' ')}`;
};
